import React, { useContext, useEffect, useState } from 'react';
import { Button, ButtonGroup, Col, Row, Table } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from 'lodash';
import { FilterDateTime, FilterSelect, FilterSwitch, Pager, ServiceThreadSpinner, SortHeader } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, storage, dateHelpers, filterHelpers, helpers, UserContext } from '../utils';

export default function RepairWorkOrderSearch(props) {
   const navigate = useNavigate();
   const { currentUser } = useContext(UserContext);
   const ALL_OPTION = { value: 0, label: "-- All --" };
   const ALL_MACHINES = { value: 0, label: "-- All --", activeMachines: "0" };
   const [data, setData] = useState([]);
   const [downloading, setDownloading] = useState(false);
   const [isEditor, setEditor] = useState(false);
   const [initialized, setInitialized] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [machines, setMachines] = useState([]);
   const [machineNums, setMachineNums] = useState([]);
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(15);
   const [problems, setProblems] = useState([]);
   const [sortDirection, setSortDirection] = useState("desc");
   const [sortField, setSortField] = useState("workOrderId")
   const [totalPages, setTotalPages] = useState();
   const [filters, setFilters] = useState([
      { filterName: "startDate", value: "" },
      { filterName: "endDate", value: "" },
      { filterName: "machineId", value: ALL_MACHINES },
      { filterName: "machineNumber", value: ALL_OPTION },
      { filterName: "problemId", value: ALL_OPTION },
      { filterName: "showComplete", value: false }
   ]);

   useEffect(() => {
      async function setDropdowns() {
         if (!currentUser) return;
         var active = false; // Indicator of if we only show active problems and their children

         api
            .fetch(`Repair/LoadRwoDropdowns/${active}`)
            .then(response => {
               if (response.data.success) {
                  const problemList = _.concat([ALL_OPTION],
                     _.map(response.data.problems, pblm => ({ value: pblm.problemId, label: pblm.problem })));
                  const machineList = _.concat([ALL_MACHINES],
                     _.map(response.data.machines, m => ({ value: m.machineId, label: m.machineName, activeMachines: m.activeMachines })));
                  return {
                     problemList: problemList,
                     machineList: machineList
                  };
               } else {
                  throw new Error(response);
               }
            }).then(async ({ problemList, machineList }) => {
               setProblems(problemList);
               setMachines(machineList);
               setMachineNums([ALL_OPTION]); // Default, but if reloading for specific machine type below will update

               // Machine # should only fill in after a machine type is selected
               let tmpFilters = JSON.parse(storage.getItem("rwoSearchFilter"));
               if (tmpFilters) {
                  let tmpId = filterHelpers.getValue(tmpFilters, "machineId");
                  if (tmpId && tmpId.value > 0) {
                     const mach = _.find(machineList, x => x.value === tmpId.value);
                     await fixMachines(mach.activeMachines);
                     setFilters(tmpFilters);
                  }
                  else {
                     setFilters(tmpFilters);
                  }
               }
               runQuery();
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => setInitialized(true))
      }
      setDropdowns();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser])

   useEffect(() => {
      if (pageSize === null) setPageSize(15);
      if (initialized && !loaded && !downloading) {
         runQuery();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded, downloading])

   function runQuery() {
      document.title = `Repair Work Orders`;
      api
         .post(`Repair/GetPaginatedRepairWorkOrders`, createPayload())
         .then(response => {
            if (response.data.success) {
               setEditor(response.data.canEdit);
               setData(response.data.orders);
               setTotalPages(_.ceil(response.data.totals / pageSize));
            } else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
         })
         .finally(() => setLoaded(true))
   }

   function createPayload() {
      let payload = {
         PageNumber: page,
         PageSize: pageSize,
         SortField: sortField,
         SortDirection: sortDirection,
      }

      let tmpFilters = JSON.parse(storage.getItem("rwoSearchFilter"));
      if (!tmpFilters)
         tmpFilters = filters;
      _.each(tmpFilters, (filter) => payload[filter.filterName] = filter.value)

      payload.machineId = payload.machineId.value;
      payload.machineNumber = payload.machineNumber.value;
      payload.problemId = payload.problemId.value;

      if (payload.startDate === "") {
         payload.startDate = null;
      }
      if (payload.endDate === "") {
         payload.endDate = null;
      }
      return payload;
   }

   function deleteWorkOrder(workOrderId, displayId) {
      confirmAlert({
         title: "Permanently Delete RWO",
         message: `This will permanently delete ${displayId}. Are you sure?`,
         buttons: [
            {
               label: "Ok",
               onClick: () => {
                  api
                     .post(`Repair/DeleteRepairWorkOrder/${workOrderId}`)
                     .then((response) => {
                        if (response.data.success) {
                           runQuery();
                        } else {
                           toast.error(response.data.message);
                        }
                     })
                     .catch(api.catchHandler);
               },
            },
            {
               label: "Cancel",
            },
         ],
      });
   }

   function editWorkOrder(workOrderId) {
      var nav = constants.ENDPOINTS.repairWorkOrder + "/" + workOrderId;
      navigate(nav);
   }

   function exportXlsx() {
      setDownloading(true)
      api
         .postGetBlob("Repair/ExportRepairWorkOrdersXLSX", createPayload())
         .then(response => {
            setDownloading(false)
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = `Repair_WOs.xlsx`
                  helpers.downloadExport(response.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
               }
            } else {
               toast.error(response.message || "There was an error downloading the file");
            }
         }).catch((error) => {
            api.catchHandler(error)
            toast.error("There was an error downloading the file");
         })
         .finally(() => setDownloading(false));
   }

   const fixMachines = async (activeMachines) => {
      // Fill in the drop-down with active machines for the selected machine
      var nums = activeMachines.split(", ");
      let newMachineList = _.concat([ALL_OPTION], _.map(nums, m => ({ value: parseInt(m, 10), label: m })));
      setMachineNums(newMachineList);
      return newMachineList;
   };

   const onMachineChange = async (newFilterValue) => {
      // Fill in the machine related drop-down lists with proper values
      const mach = _.find(machines, x => x.value === newFilterValue.value);
      let newMachList = await fixMachines(mach.activeMachines);

      // Set the machine dependent filters to their defaults
      const numFilter = _.find(newMachList, m => m.value === 0);

      let newFilters = filterHelpers.getFilters(filters, ({ filterName: "machineId", value: mach }));
      newFilters = filterHelpers.getFilters(newFilters, ({ filterName: "machineNumber", value: numFilter }));
      setFilters(newFilters);
      storage.setItem("rwoSearchFilter", JSON.stringify(newFilters));
      setPage(1);
      setLoaded(false);
   };

   function onPageChange(page) {
      setPage(page);
      setLoaded(false);
   }

   function onSimpleChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);
      storage.setItem("rwoSearchFilter", JSON.stringify(newFilters));
      setPage(1);
      setLoaded(false);
   }

   function onSimpleFilterChange(newFilterValue) {
      let tmpFilter = { value: newFilterValue.value, label: newFilterValue.label };
      let newFilters = filterHelpers.getFilters(filters, { filterName: newFilterValue.filterName, value: tmpFilter });
      setFilters(newFilters);
      storage.setItem("rwoSearchFilter", JSON.stringify(newFilters));
      setPage(1);
      setLoaded(false);
   }

   function onSortChange(field, direction) {
      if (field !== sortField) {
         setSortField(field);
         setSortDirection("asc")
      } else {
         setSortDirection(direction);
      }
      onPageChange(1);
   }

   function printReport() {
      setDownloading(true);
      api
         .postDownloadBinary(
            `Report/RepairWorkOrderReport`,
            createPayload(),
            constants.MIME_TYPES.pdf,
            `Repair_Work_Orders_${dateHelpers.toMDYDateString(new Date())}.pdf`,
            "There was an error downloading the repair work orders pdf"
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   function printSummaryReport() {
      setDownloading(true);
      api
         .postGetBlob(`Report/RepairWorkOrderSummaryReport`, createPayload())
         .then(r => {
            if (r.data.success === false) {
               if (_.isString(r.data.message)) {
                  toast.error(r.data.message);
               }
               return;
            }
            if (r.data) {
               const XLSX_FILENAME = `RWO_Summary_${dateHelpers.toMDYDateString(new Date())}.xlsx`
               helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
            } else {
               toast.error(r.message || "Error downloading RWO Summary report");
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   return (
      <DesktopContainer screenName="Repair Work Orders">
         <div className="filter-header">
            <Row>
               <Col xs={{ size: 10, offset: 2 }}>
                  <Button
                     title="Create New RWO"
                     className="shredButton"
                     onClick={() => editWorkOrder(0)}
                     disabled={false}>
                     Create New RWO
                  </Button>
               </Col>
            </Row>
            <Row>
               <Col xs={{ size: 10, offset: 1 }}>
                  <hr />
               </Col>
            </Row>
            <Row className="justify-content-center">
               <Col xs={6}>
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>
                        Machine:
                     </Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="machineId"
                           options={machines}
                           value={filterHelpers.getValue(filters, "machineId")}
                           onChangeCallback={e => onMachineChange(e)}
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>
                        Machine #:
                     </Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="machineNumber"
                           options={machineNums}
                           value={filterHelpers.getValue(filters, "machineNumber")}
                           onChangeCallback={e => onSimpleFilterChange(e)}
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>
                        Problem:
                     </Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="problemId"
                           options={problems}
                           value={filterHelpers.getValue(filters, "problemId")}
                           onChangeCallback={e => onSimpleFilterChange(e)}
                        />
                     </Col>
                  </Row>
               </Col>
               <Col xs={6}>
                  <Row className="align-items-center my-1">
                     <Row className="align-items-center my-1">
                        <Col className="text-end text-white h5" xs={3}>
                           Start Open Date:
                        </Col>
                        <Col xs={8}>
                           <FilterDateTime
                              filterName="startDate"
                              value={filterHelpers.getValue(filters, "startDate")}
                              onChangeCallback={e => onSimpleChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center my-1">
                        <Col className="text-end text-white h5" xs={3}>
                           End Open Date:
                        </Col>
                        <Col xs={8}>
                           <FilterDateTime
                              filterName="endDate"
                              value={filterHelpers.getValue(filters, "endDate")}
                              onChangeCallback={e => onSimpleChange(e)}
                           />
                        </Col>
                     </Row>
                     <Col className="text-end text-white h5" xs={4}>
                        Show Completed:
                     </Col>
                     <Col xs={8}>
                        <FilterSwitch
                           filterName="showComplete"
                           value={filterHelpers.getValue(filters, "showComplete")}
                           onChangeCallback={e => onSimpleChange(e)}
                        />
                     </Col>
                  </Row>
               </Col>
            </Row>
         </div>
         <br />

         <ServiceThreadSpinner on={!loaded}>
            {data && data.length
               ? (<Row className="px-5">
                  <Row className="align-items-center my-1">
                     <Col className="transparent-background">
                        <Pager
                           callBack={onPageChange}
                           sizeCallback={null}
                           pageNumber={page}
                           pageSize={pageSize ? pageSize : 15}
                           totalPages={totalPages}
                           downloading={downloading}
                        />
                     </Col>
                     <Col>
                        <Button
                           title="Download as .xlsx"
                           className="shredButtonExcel float-end mx-1"
                           onClick={exportXlsx}
                           disabled={downloading}
                        >
                           <FontAwesomeIcon icon="file-excel" size="lg" />
                        </Button>
                        <Button
                           className="shredButton float-end mx-1"
                           title="Print Report"
                           onClick={() => printReport()}
                        >Print Report
                        </Button>
                        <Button
                           className="shredButton float-end mx-1"
                           title="Print Report"
                           onClick={printSummaryReport}
                        >Print Summary
                        </Button>
                     </Col>
                  </Row>

                  <Table striped className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th>
                              <SortHeader
                                 displayName="Work Order"
                                 field="workOrderId"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "workOrderId"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className={"w-10"}>
                              <SortHeader
                                 displayName="Open Date"
                                 field="enteredDate"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "enteredDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Lead"
                                 field="operatorInitials"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "operatorInitials"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Machine"
                                 field="machineName"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "machineName"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Machine#"
                                 field="machineNumber"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "machineNumber"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Problem"
                                 field="problem"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "problem"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Side"
                                 field="machineSide"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "machineSide"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Spindle"
                                 field="spindle"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "spindle"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Comments"
                                 field="comments"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "comments"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Repair Comments"
                                 field="repairComments"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "repairComments"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Repair Date"
                                 field="repairDate"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "repairDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Tech"
                                 field="mechanicInitials"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "mechanicInitials"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Status"
                                 field="status"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "status"}
                                 callback={onSortChange}
                              />
                           </th>
                           {isEditor &&
                              (<th width="2%" />)}
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(data, (datum, index) => {
                           return (
                              <tr
                                 key={index}
                                 className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                              >
                                 <td className="data-cell">
                                    <Button
                                       className="shredLink"
                                       children={datum.displayId}
                                       onClick={() => editWorkOrder(datum.workOrderId)}
                                    />
                                 </td>
                                 <td
                                    className="data-cell"
                                    children={dateHelpers.dateTimeFormat(datum.enteredDate, "MM-DD-YYYY")}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.operatorInitials}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.machineName}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.machineNumber}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.problem}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.machineSide}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.spindle}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.comments ? datum.comments : ""}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.repairComments ? datum.repairComments : ""}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.repairDate ? dateHelpers.dateTimeFormat(datum.repairDate, "MM-DD-YYYY") : ""}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.mechanicInitials ? datum.mechanicInitials : ""}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.status}
                                 />
                                 {isEditor &&
                                    (<td className="data-cell">
                                       <ButtonGroup>
                                          <Button
                                             className="projectInfo"
                                             size="sm"
                                             title="Edit"
                                             color="info"
                                             onClick={() => editWorkOrder(datum.workOrderId)}
                                          >
                                             <FontAwesomeIcon className="whiteIcon" icon="edit" />
                                          </Button>
                                          <Button
                                             style={{ backgroundColor: "red" }}
                                             size="sm"
                                             className="projectDanger"
                                             title="Delete"
                                             onClick={() => deleteWorkOrder(datum.workOrderId, datum.displayId)}
                                          >
                                             <FontAwesomeIcon className="whiteIcon" icon="times-circle" />
                                          </Button>
                                       </ButtonGroup>
                                    </td>)}
                              </tr>
                           )
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan={13} />
                           {isEditor &&
                              (<td />)}
                        </tr>
                     </tfoot>
                  </Table>
               </Row>)
               : <div className="h5 text-center mt-3">No data found</div>
            }
         </ServiceThreadSpinner>
      </DesktopContainer >
   );
}